import React from 'react';
import {
  observable,
  action,
  computed,
  makeObservable,
  runInAction,
} from 'mobx';
import RootStore from './RootStore';
import { IAttachment } from '../interfaces/attachment.interface';
import { ValidationUtil } from 'rmesse-utils';
import { AttachmentApi } from '../api/attachment.api';
import { RmesseValidationWrapper } from '../utils/rmesse-validation-wrapper';

export default class ChatInputStore {
  private static readonly MAX_ATTACHMENT_FILE_NUM = 3;
  private static readonly CHAT_INPUT_LINE_HEIGHT = 18;
  private static readonly DEFAULT_INPUT_ROWS = 3;
  private static readonly DEFAULT_MAX_INPUT_ROWS = 18;
  private static readonly CHAT_INPUT_PADDING_HEIGHT = 18;

  @observable private inputMessageList: Map<string, string> = new Map();
  @observable private validationMessageList: Map<string, string[]> = new Map();
  @observable private warningMessageList: Map<string, string[]> = new Map();
  @observable private expandedStatusList: Map<string, boolean> = new Map();
  @observable private attachmentsList: Map<string, IAttachment[]> = new Map();
  @observable private isAttachmentsListUploadDisabledList: Map<
    string,
    boolean
  > = new Map<string, boolean>();

  @observable isFileDragging: boolean = false;
  @observable showChatInputScrollBar: boolean = false;
  @observable inputTextAreaHeight: number =
    ChatInputStore.CHAT_INPUT_LINE_HEIGHT * ChatInputStore.DEFAULT_INPUT_ROWS +
    ChatInputStore.CHAT_INPUT_PADDING_HEIGHT;
  @observable chatInput: React.RefObject<HTMLTextAreaElement> =
    React.createRef();
  @observable isFileUploading: boolean = false;

  @computed
  get activeInquiryId(): string {
    return this.rootStore.inquiryStore.activeInquiryId;
  }

  // for future expansion...?
  // @computed
  // get isSubmitDisabled(): boolean {
  //   const { isLoadingMessageData } = this.rootStore.inquiryPageStore;
  //   const { validateCategories } = this.rootStore.inquiryStore;
  //   return (
  //     isLoadingMessageData ||
  //     this.validationMessage.length > 0 ||
  //     validateCategories ||
  //     this.isFileUploading ||
  //     !this.inputMessage
  //   );
  // }

  @computed
  get canUpload(): boolean {
    return (
      this.attachments.length <= ChatInputStore.MAX_ATTACHMENT_FILE_NUM &&
      //this.validationMessage.length === 0 && // (TO CONFIRM) this condition no need to check
      !this.isFileUploading
    );
  }

  @computed
  get isAttachmentsListUploadDisabled(): boolean {
    return (
      this.isAttachmentsListUploadDisabledList.get(this.activeInquiryId) ||
      false
    );
  }

  @action
  // FIXME: for same validation policy, it should be duplicate code as rmp-front
  setAttachmentsListUploadDisabled = (disabled: boolean) => {
    this.isAttachmentsListUploadDisabledList.set(
      this.activeInquiryId,
      disabled,
    );
  };

  @action
  setIsFileDragging = (dragging: boolean) => {
    this.isFileDragging = dragging;
  };

  @computed
  get isExpanded(): boolean {
    return this.expandedStatusList.get(this.activeInquiryId) || false;
  }

  @action
  setExpanded = (expanded: boolean) => {
    this.expandedStatusList.set(this.activeInquiryId, expanded);
  };

  @computed
  get inputMessage(): string {
    return this.inputMessageList.get(this.activeInquiryId) || '';
  }

  @action
  setInputMessage = (inputMessage: string) => {
    this.inputMessageList.set(this.activeInquiryId, inputMessage);
    this.setWarningMessage(
      ValidationUtil.warningValidateInputMessageOnChange(this.inputMessage),
    );
    this.adjustInputTextareaHeight();
  };

  @action
  adjustInputTextareaHeight = () => {
    const minRows = ChatInputStore.DEFAULT_INPUT_ROWS;
    const maxRows = ChatInputStore.DEFAULT_MAX_INPUT_ROWS;
    const currentRows = this.inputMessage.split('\n').length;

    this.setShowChatInputScrollBar(currentRows >= maxRows);

    const rows =
      currentRows <= minRows
        ? minRows
        : currentRows >= maxRows
        ? maxRows
        : currentRows;

    this.setInputTextAreaHeight(
      rows * ChatInputStore.CHAT_INPUT_LINE_HEIGHT +
        ChatInputStore.CHAT_INPUT_PADDING_HEIGHT,
    );
  };

  @action
  setInputTextAreaHeight = (height: number) => {
    runInAction(() => {
      this.inputTextAreaHeight = height;
    });
  };

  @action
  setShowChatInputScrollBar = (show: boolean) => {
    runInAction(() => {
      this.showChatInputScrollBar = show;
    });
  };

  @computed
  get attachments(): IAttachment[] {
    if (!this.attachmentsList.get(this.activeInquiryId)) {
      this.attachmentsList.set(this.activeInquiryId, []);
    }
    return this.attachmentsList.get(this.activeInquiryId) || [];
  }

  @action
  setAttachments = (attachments: IAttachment[]) => {
    this.attachmentsList.set(this.activeInquiryId, attachments);
  };

  @computed
  get validationMessage(): string[] {
    if (!this.validationMessageList.get(this.activeInquiryId)) {
      this.validationMessageList.set(this.activeInquiryId, []);
    }
    return this.validationMessageList.get(this.activeInquiryId) || [];
  }

  @action
  setValidationMessage = (validationMessage: string[]) => {
    this.validationMessageList.set(this.activeInquiryId, validationMessage);
  };

  @computed
  get warningMessage(): string[] {
    if (!this.warningMessageList.get(this.activeInquiryId)) {
      this.warningMessageList.set(this.activeInquiryId, []);
    }
    return this.warningMessageList.get(this.activeInquiryId) || [];
  }

  @action
  setWarningMessage = (warningMessage: string[]) => {
    this.warningMessageList.set(this.activeInquiryId, warningMessage);
  };

  @action
  // FIXME: for same validation policy, it should be duplicate code as rmp-front
  onSubmit = async () => {
    if (!this.inputAreaValidation()) {
      return;
    }
    await this.rootStore.messageStore.postMessageData();
    this.resetInput();
  };

  private inputAreaValidation() {
    const errors = ValidationUtil.validateInputMessage(this.inputMessage);
    this.setValidationMessage(
      errors.filter(
        (error) => error !== ValidationUtil.ERR_MSG_DENIED_URL_INCLUDED,
      ),
    );
    return this.validationMessage.length === 0;
  }

  @action
  resetInput = () => {
    this.setInputMessage('');
    this.setAttachments([]);
    this.setValidationMessage([]);
    this.setExpanded(false);
    this.setAttachmentsListUploadDisabled(false);
  };

  @action
  // FIXME: for same validation policy, it should be duplicate code as rmp-front
  fileUploadDocumentRequest = async ({ file }: { file: File }) => {
    if (this.validateAttachFile(file)) {
      this.isFileUploading = true;
      this.setAttachmentsListUploadDisabled(true);
      try {
        const responseData = await AttachmentApi.postAttachment(file);
        console.log({ responseData });
        const hasThumbnail = responseData.result[0].thumbnail_object_id
          ? true
          : false;
        console.log({ hasThumbnail });

        const attachment: IAttachment = {
          fileName: file.name,
          fileId: responseData.result[0].object_id,
          size: file.size,
          hasThumbnail: hasThumbnail,
        };
        this.attachments.push(attachment);
      } catch (e) {
        this.validationMessage.push(
          `${file.name}: ファイルをアップロードできません`,
        );
      } finally {
        this.setAttachmentsListUploadDisabled(false);
        this.isFileUploading = false;
      }
    }

    if (this.attachments.length === ChatInputStore.MAX_ATTACHMENT_FILE_NUM) {
      this.setAttachmentsListUploadDisabled(true);
    }

    this.focusChatInput();
  };

  // FIXME: for same validation policy, it should be duplicate code as rmp-front
  private validateAttachFile(file: File) {
    const currentAttachmentValidationErrors =
      RmesseValidationWrapper.validateAttachFile(file);

    const fileSizeList = this.attachments.map((attachment) => attachment.size);
    fileSizeList.push(file.size);

    const fileAttachmentErrors = Array.from(
      new Set(
        currentAttachmentValidationErrors.concat(
          ValidationUtil.validateAttachFileList(fileSizeList),
        ),
      ),
    );

    const fileCanBeAttached = fileAttachmentErrors.length === 0;

    this.setValidationMessage(
      fileAttachmentErrors.concat(
        ValidationUtil.validateInputMessageOnChange(this.inputMessage),
      ),
    );

    return fileCanBeAttached;
  }

  @action
  onDeleteAttachFile = (attachemntFile: IAttachment) => {
    // this.setValidationMessage(
    //   ValidationUtil.validateInputMessageOnChange(this.inputMessage),
    // );
    this.attachments.splice(this.attachments.indexOf(attachemntFile), 1);
    if (this.attachments.length < ChatInputStore.MAX_ATTACHMENT_FILE_NUM) {
      this.setAttachmentsListUploadDisabled(false);
    }
    this.focusChatInput();
  };

  @action
  onClickAlertClose = (i: number) => {
    const newValidationMessage = this.validationMessage.filter(
      (value, index, array) => {
        return index !== i;
      },
    );
    this.setValidationMessage(newValidationMessage);
  };

  @action
  onExpandInputArea = () => {
    this.setExpanded(!this.isExpanded);
    this.adjustInputTextareaHeight();
  };

  @action
  public focusChatInput = () => {
    if (this.chatInput && this.chatInput.current) {
      this.chatInput.current.focus();
    }
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this);
  }
  private rootStore: RootStore;
}
